import _ from "lodash";
import client from "lib/apolloClient/client";

import { dispatchError } from "Utils";
import { getUserInfo } from "Login/modules/LoginModule";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { handleToggleLoader } from "SchoolSetup/routes/LoginConfigurationManager/routes/LoginPageConfiguration/modules/LoginPageConfigurationModule";
import {
  manageUserMfaMutation,
  upsertMfaPoliciesMutation,
  completeUserMfaMutation,
  initiateMfaSetupMutation,
  disconnectUserMfaMutation,
  togglePrimaryMfaMethodMutation,
  verifyUserMfaMutation,
  sendOtpForMfaVerificationMutation,
} from "./MultiFactorAuthenticationMutation";
import { formatErrorMessage } from "./MultiFactorAuthenticationUtils";
import {
  getMfaEnforcementQuery,
  getUserMfaStatusQuery,
} from "./MultiFactorAuthenticationQuery";

export const initiateMfaSetup = input => {
  return async dispatch => {
    dispatch(handleToggleLoader(true));
    try {
      const result = await client.mutate({
        mutation: initiateMfaSetupMutation,
        variables: {
          input,
        },
      });
      const data = _.get(result, "data.platform.initiateMfaSetup");
      if (!_.get(data, "isSuccess", false)) {
        const message = _.get(data, "message", "");
        dispatch(
          setToastMsg({
            msg: formatErrorMessage(message),
            type: "error",
          })
        );
      }
      return data;
    } catch (e) {
      dispatchError({ dispatch, e });
    } finally {
      dispatch(handleToggleLoader(false));
    }
  };
};

export const completeUserMfa = input => {
  return async dispatch => {
    dispatch(handleToggleLoader(true));
    const userInfo = getUserInfo();
    const id = userInfo.id;
    const type = _.get(userInfo, "userEntityType", "");
    try {
      const result = await client.mutate({
        mutation: completeUserMfaMutation,
        variables: {
          input,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getUserMfaStatusQuery,
            variables: {
              id,
              type,
            },
          },
        ],
      });
      const data = _.get(result, "data.platform.completeMfaSetup");
      const isSuccess = _.get(data, "isSuccess", false);
      if (!isSuccess) {
        const message = _.get(data, "message", "");
        dispatch(
          setToastMsg({
            msg: formatErrorMessage(message),
            type: "error",
          })
        );
      } else {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:2fa_setup_completed",
            type: "success",
          })
        );
      }
      return isSuccess;
    } catch (e) {
      dispatchError({ dispatch, e });
    } finally {
      dispatch(handleToggleLoader(false));
    }
  };
};

export const disconnectUserMfa = input => {
  return async dispatch => {
    dispatch(handleToggleLoader(true));
    const userInfo = getUserInfo();
    const id = userInfo.id;
    const type = _.get(userInfo, "userEntityType", "");
    try {
      const result = await client.mutate({
        mutation: disconnectUserMfaMutation,
        variables: {
          input,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getUserMfaStatusQuery,
            variables: {
              id,
              type,
            },
          },
        ],
      });
      const data = _.get(result, "data.platform.disconnectUserMfa");
      const isSuccess = _.get(data, "isSuccess", false);
      if (!isSuccess) {
        const message = _.get(data, "message", "");
        dispatch(
          setToastMsg({
            msg: formatErrorMessage(message),
            type: "error",
          })
        );
      } else {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:disabled_2fa",
            type: "success",
          })
        );
      }
      return isSuccess;
    } catch (e) {
      dispatchError({ dispatch, e });
    } finally {
      dispatch(handleToggleLoader(false));
    }
  };
};

export const togglePrimaryMfaMethod = input => {
  return async dispatch => {
    dispatch(handleToggleLoader(true));
    const userInfo = getUserInfo();
    const id = userInfo.id;
    const type = _.get(userInfo, "userEntityType", "");
    try {
      const result = await client.mutate({
        mutation: togglePrimaryMfaMethodMutation,
        variables: {
          input,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getUserMfaStatusQuery,
            variables: {
              id,
              type,
            },
          },
        ],
      });
      const data = _.get(result, "data.platform.togglePrimaryMfaMethod");
      const isSuccess = _.get(data, "isSuccess", false);
      if (!isSuccess) {
        const message = _.get(data, "message", "");
        dispatch(
          setToastMsg({
            msg: formatErrorMessage(message),
            type: "error",
          })
        );
      } else {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:method_marked_as_primary_successfully",
            type: "success",
          })
        );
      }
      return isSuccess;
    } catch (e) {
      dispatchError({ dispatch, e });
    } finally {
      dispatch(handleToggleLoader(false));
    }
  };
};

export const verifyUserMfa = input => {
  return async dispatch => {
    dispatch(handleToggleLoader(true));
    try {
      const result = await client.mutate({
        mutation: verifyUserMfaMutation,
        variables: {
          input,
        },
      });
      const data = _.get(result, "data.platform.verifyUserMfa");
      if (!_.get(data, "isSuccess", false)) {
        const message = _.get(data, "message", "");
        dispatch(
          setToastMsg({
            msg: formatErrorMessage(message),
            type: "error",
          })
        );
        return;
      }
      const jwt = _.get(data, "jwt", null);
      return jwt;
    } catch (e) {
      dispatchError({ dispatch, e });
    } finally {
      dispatch(handleToggleLoader(false));
    }
  };
};

export const sendOtpForMfaVerification = input => {
  return async dispatch => {
    try {
      const result = await client.mutate({
        mutation: sendOtpForMfaVerificationMutation,
        variables: {
          input,
        },
      });
      const data = _.get(result, "data.platform.sendOtpForMfaVerification");
      if (!_.get(data, "isSuccess", false)) {
        const message = _.get(data, "message", "");
        dispatch(
          setToastMsg({
            msg: formatErrorMessage(message),
            type: "error",
          })
        );
      }
      return data;
    } catch (e) {
      dispatchError({ dispatch, e });
    }
  };
};

const executeMfaMutation = ({ mutation, dataPath, mutationOptions = {} }) => {
  return async dispatch => {
    dispatch(handleToggleLoader(true));

    try {
      const result = await client.mutate({ mutation, ...mutationOptions });

      const data = _.get(result, dataPath, {});
      const isSuccess = _.get(data, "isSuccess", false);

      if (isSuccess) {
        dispatch(
          setToastMsg({
            type: "success",
            msg: "toastMsgs:entity_updated_successfully",
            locale_params: [
              {
                key: "entity",
                value: "common:2fa_enforcement",
                isPlainText: false,
              },
            ],
          })
        );
      } else {
        const message = _.get(data, "message", "");
        dispatch(
          setToastMsg({
            msg: formatErrorMessage(message),
            type: "error",
          })
        );
      }

      return isSuccess;
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return false;
    } finally {
      dispatch(handleToggleLoader(false));
    }
  };
};

export const manageUserMfa = input => {
  return executeMfaMutation({
    mutation: manageUserMfaMutation,
    dataPath: "data.platform.manageUserMfa",
    mutationOptions: { variables: { input } },
  });
};

export const manageMfaEnforcement = (input, organizationId) => {
  return executeMfaMutation({
    mutation: upsertMfaPoliciesMutation,
    dataPath: "data.organizationManagement.upsertMfaPolicies",
    mutationOptions: {
      variables: { input },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: getMfaEnforcementQuery,
          variables: { id: organizationId },
        },
      ],
    },
  });
};
